import { useQuery } from '@tanstack/react-query';
import { Star, MessageSquare, Hourglass } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import StatCard from '@/components/admin/dashboard/StatCard';
import api from '@/lib/api';

/**
 * Mirrors the `/admin/reviews/analytics` handler
 * (functions/src/routes/admin/reviews.ts → GET /analytics). The summary lives
 * under `stats`; its COUNT/AVG columns serialize as strings from postgres.js,
 * and `overall_avg_rating` is null when there are no reviews.
 */
interface ReviewStats {
  total_reviews: number | string;
  pending_count: number | string;
  approved_count: number | string;
  overall_avg_rating: number | string | null;
  verified_count: number | string;
  avg_helpful_count: number | string | null;
}

interface ReviewsAnalyticsResponse {
  rating_over_time: unknown[];
  rating_distribution: unknown[];
  top_products: unknown[];
  stats: ReviewStats;
}

export default function ReviewsSummaryWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'reviews', 'analytics'],
    queryFn: () => api.get<ReviewsAnalyticsResponse>('/admin/reviews/analytics'),
    staleTime: 60000,
  });

  const totalReviews = data ? Number(data.stats.total_reviews) : 0;
  const pending = data ? Number(data.stats.pending_count) : 0;
  const avgRating =
    data && data.stats.overall_avg_rating != null
      ? Number(data.stats.overall_avg_rating).toFixed(1)
      : '—';

  return (
    <WidgetShell
      title="Reviews"
      icon={<Star />}
      to="/admin/reviews/analytics"
      isLoading={isLoading}
      error={error}
      isEmpty={!!data && totalReviews === 0}
      emptyIcon={<Star />}
      emptyMessage="No reviews yet."
      skeletonClassName="h-40"
    >
      {data && (
        <div className="space-y-3">
          <StatCard
            title="Average rating"
            value={avgRating}
            icon={<Star />}
            tone="amber"
            subtitle="out of 5"
          />
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              title="Total reviews"
              value={totalReviews.toLocaleString()}
              icon={<MessageSquare />}
              tone="blue"
            />
            <StatCard
              title="Pending"
              value={pending.toLocaleString()}
              icon={<Hourglass />}
              tone="orange"
              subtitle="Awaiting review"
              to="/admin/reviews"
            />
          </div>
        </div>
      )}
    </WidgetShell>
  );
}
